import type { ErrorDefinition } from './types';

/**
 * Comprehensive mapping of Supabase auth error codes to friendly messages.
 * Each message is crafted to be empathetic, clear, and actionable.
 */
export const AUTH_ERRORS: Record<string, ErrorDefinition> = {
  // ═══════════════════════════════════════════════════════════════════════════
  // OTP / Verification Code Errors
  // ═══════════════════════════════════════════════════════════════════════════

  otp_expired: {
    title: 'Code expired',
    message:
      'The verification code you entered has expired. These codes are only valid for a short time to keep your account secure.',
    action: 'Tap "Resend code" to get a fresh one.',
    retryable: true,
    category: 'authentication',
    recoverySuggestions: [
      'Check your email for the most recent code',
      'Request a new code if needed',
    ],
  },

  otp_disabled: {
    title: 'Email sign-in unavailable',
    message:
      'Email verification is temporarily disabled. We apologize for the inconvenience.',
    action: 'Please try again later or contact support.',
    retryable: false,
    category: 'server',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Rate Limiting
  // ═══════════════════════════════════════════════════════════════════════════

  over_email_send_rate_limit: {
    title: 'Too many attempts',
    message:
      "We've sent several codes to this email recently. This limit helps protect your account.",
    action: 'Please wait a few minutes before requesting another code.',
    retryable: true,
    category: 'rate_limit',
    recoverySuggestions: [
      'Check your spam folder for existing codes',
      'Wait 2-3 minutes before trying again',
    ],
  },

  over_request_rate_limit: {
    title: 'Slow down',
    message:
      "You're sending requests too quickly. This limit helps keep everyone's accounts safe.",
    action: 'Take a short break and try again in a moment.',
    retryable: true,
    retryAfterSeconds: 30,
    category: 'rate_limit',
  },

  over_sms_send_rate_limit: {
    title: 'Too many texts sent',
    message:
      "We've sent several codes to this phone number recently. This is to protect your account.",
    action: 'Please wait a few minutes before requesting another code.',
    retryable: true,
    retryAfterSeconds: 60,
    category: 'rate_limit',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Email Validation
  // ═══════════════════════════════════════════════════════════════════════════

  email_address_invalid: {
    title: 'Invalid email',
    message:
      "We couldn't recognize that email address. Please double-check for typos.",
    action: 'Enter a valid email address to continue.',
    retryable: true,
    category: 'validation',
    recoverySuggestions: [
      'Check for typos in your email',
      'Make sure to include @ and a domain (like gmail.com)',
    ],
  },

  email_address_not_authorized: {
    title: 'Email not allowed',
    message: "This email address can't be used to sign up right now.",
    action: 'Please try a different email address.',
    retryable: true,
    category: 'validation',
  },

  validation_failed: {
    title: 'Something looks off',
    message: "The information you entered doesn't look quite right.",
    action: 'Please review your entry and try again.',
    retryable: true,
    category: 'validation',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Account Status
  // ═══════════════════════════════════════════════════════════════════════════

  email_exists: {
    title: 'Email already registered',
    message: 'Good news! This email is already linked to an account.',
    action: "We'll send you a code to sign in.",
    retryable: true,
    category: 'account',
  },

  email_not_confirmed: {
    title: 'Email not verified',
    message:
      "Your email hasn't been confirmed yet. We need to verify it's really you.",
    action: 'Check your inbox for a verification email, or request a new one.',
    retryable: true,
    category: 'account',
    recoverySuggestions: [
      'Check your spam/junk folder',
      'Request a new verification email',
    ],
  },

  user_not_found: {
    title: 'Account not found',
    message:
      "We couldn't find an account with this email. But no worries!",
    action: "We'll create one for you when you continue.",
    retryable: true,
    category: 'account',
  },

  user_already_exists: {
    title: 'Account exists',
    message: 'An account with this information already exists.',
    action: 'Try signing in instead, or use a different email.',
    retryable: true,
    category: 'account',
  },

  user_banned: {
    title: 'Account suspended',
    message:
      'This account has been suspended. We understand this is frustrating.',
    action: 'Please contact our support team for assistance.',
    retryable: false,
    category: 'account',
    recoverySuggestions: ['Contact support for help with your account'],
  },

  signup_disabled: {
    title: 'Sign-up unavailable',
    message:
      "We're not accepting new accounts right now. We're working on making room for more users.",
    action: 'Please check back soon.',
    retryable: false,
    category: 'server',
  },

  email_provider_disabled: {
    title: 'Email sign-up disabled',
    message: 'Email sign-up is currently unavailable.',
    action: 'Please try signing in with Apple or Google.',
    retryable: false,
    category: 'server',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Authentication Failures
  // ═══════════════════════════════════════════════════════════════════════════

  invalid_credentials: {
    title: 'Incorrect code',
    message:
      "That code doesn't match what we sent. It happens to the best of us!",
    action: 'Please check the code and try again, or request a new one.',
    retryable: true,
    category: 'authentication',
    recoverySuggestions: [
      'Double-check each digit',
      'Use the most recent code from your email',
      'Request a fresh code if needed',
    ],
  },

  captcha_failed: {
    title: 'Security check failed',
    message:
      "We couldn't verify you're human. This sometimes happens with VPNs or certain browsers.",
    action: 'Please try again. If it keeps happening, try a different network.',
    retryable: true,
    category: 'authentication',
    recoverySuggestions: [
      'Disable VPN if you have one active',
      'Try on a different network',
    ],
  },

  session_expired: {
    title: 'Session expired',
    message: 'Your session has expired. This happens after a period of inactivity.',
    action: 'Please sign in again to continue.',
    retryable: true,
    category: 'authentication',
  },

  session_not_found: {
    title: 'Session not found',
    message: "We couldn't find your session. You may have signed out in another window.",
    action: 'Please sign in again.',
    retryable: true,
    category: 'authentication',
  },

  refresh_token_not_found: {
    title: 'Please sign in again',
    message: 'Your login session has ended.',
    action: 'Sign in again to continue where you left off.',
    retryable: true,
    category: 'authentication',
  },

  refresh_token_already_used: {
    title: 'Session conflict',
    message:
      'It looks like you may be signed in on another device. For security, we signed you out here.',
    action: 'Please sign in again.',
    retryable: true,
    category: 'authentication',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // OAuth / Social Login
  // ═══════════════════════════════════════════════════════════════════════════

  provider_disabled: {
    title: 'Sign-in method unavailable',
    message: 'This sign-in method is temporarily unavailable.',
    action: 'Please try a different sign-in option.',
    retryable: false,
    category: 'server',
  },

  oauth_provider_not_supported: {
    title: 'Not supported',
    message: "This sign-in provider isn't available right now.",
    action: 'Please use email or another sign-in method.',
    retryable: false,
    category: 'server',
  },

  bad_oauth_state: {
    title: 'Sign-in interrupted',
    message:
      'Something went wrong during sign-in. This can happen if you took too long or refreshed the page.',
    action: 'Please try signing in again.',
    retryable: true,
    category: 'authentication',
  },

  bad_oauth_callback: {
    title: 'Sign-in failed',
    message:
      "We couldn't complete your sign-in with this provider.",
    action: 'Please try again or use a different sign-in method.',
    retryable: true,
    category: 'authentication',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Server / System Errors
  // ═══════════════════════════════════════════════════════════════════════════

  unexpected_failure: {
    title: 'Something went wrong',
    message:
      "We hit an unexpected snag on our end. It's not you, it's us.",
    action: "Please try again in a moment. We're on it.",
    retryable: true,
    retryAfterSeconds: 5,
    category: 'server',
  },

  request_timeout: {
    title: 'Taking too long',
    message:
      'The request is taking longer than expected. This might be a temporary issue.',
    action: 'Please check your connection and try again.',
    retryable: true,
    category: 'network',
    recoverySuggestions: [
      'Check your internet connection',
      'Try again in a few seconds',
    ],
  },

  bad_json: {
    title: 'Request error',
    message: 'There was a problem with the request.',
    action: 'Please try again.',
    retryable: true,
    category: 'server',
  },

  bad_jwt: {
    title: 'Authentication error',
    message: 'There was a problem verifying your identity.',
    action: 'Please sign in again.',
    retryable: true,
    category: 'authentication',
  },

  conflict: {
    title: 'Please try again',
    message: 'Your request conflicted with another. This is rare but can happen.',
    action: 'Please wait a moment and try again.',
    retryable: true,
    retryAfterSeconds: 2,
    category: 'server',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Invite / Magic Link
  // ═══════════════════════════════════════════════════════════════════════════

  invite_not_found: {
    title: 'Invite expired',
    message:
      'This invite link has expired or has already been used.',
    action: 'Please request a new invite.',
    retryable: false,
    category: 'authentication',
  },

  flow_state_expired: {
    title: 'Link expired',
    message: 'This sign-in link has expired for security reasons.',
    action: 'Please request a new sign-in link.',
    retryable: true,
    category: 'authentication',
  },

  flow_state_not_found: {
    title: 'Link invalid',
    message: "This sign-in link is no longer valid. It may have expired.",
    action: 'Please request a new sign-in link.',
    retryable: true,
    category: 'authentication',
  },
};
